import { onBeforeUnmount, onMounted, ref } from 'vue'
import _debounce from 'lodash/debounce.js'
import { useDebugger } from '@/shared/composables/useDebugger.js'

const BREAKPOINT_KEYS = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const
export type BreakpointKey = (typeof BREAKPOINT_KEYS)[number]

export const useScreenWidth = () => {
  const { $debug } = useDebugger()
  // SSR-safe: `window` doesn't exist on the server. Reading window.innerWidth here
  // crashed SSR render of every page using this composable (ReferenceError: window
  // is not defined) → blank page + hydration mismatch. Start at 0 on both server
  // and client (so hydration matches), then set the real width in onMounted.
  const screenWidth = ref(0)
  const tailwindScreens = ref<Partial<Record<BreakpointKey, number>>>({})

  const updateTailwindScreens = () => {
    if (typeof window === 'undefined') return

    const style = getComputedStyle(document.documentElement)
    const screens: Partial<Record<BreakpointKey, number>> = {}

    BREAKPOINT_KEYS.forEach((key) => {
      const value = style.getPropertyValue(`--breakpoint-${key}`).trim()
      if (value) {
        let pxValue = 0
        if (value.endsWith('px')) {
          pxValue = Number(value.replace('px', ''))
        } else if (value.endsWith('rem')) {
          pxValue = Number(value.replace('rem', '')) * 16
        } else {
          // Fallback or other unit
          pxValue = parseInt(value, 10)
        }

        if (!isNaN(pxValue)) {
          screens[key] = pxValue
        }
      }
    })
    tailwindScreens.value = screens
  }

  const recalculateScreenWidth = () => {
    screenWidth.value = window.innerWidth
  }

  const debounceRecalculateScreenWidth = _debounce(() => {
    recalculateScreenWidth()
    $debug(
      `Debounce - Screen width recalculated, width: ${screenWidth.value}px`,
    )
  }, 1000)

  onMounted(() => {
    recalculateScreenWidth()
    updateTailwindScreens()
    window.addEventListener('resize', debounceRecalculateScreenWidth)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', debounceRecalculateScreenWidth)
  })

  return {
    tailwindScreens, // Now a ref instead of computed, populated on mount
    screenWidth,
  }
}
